/*
 * Project: bellcloud
 * File: packages/ui/components/common/map/GoogleMap.tsx
 * Author: SWAN
 * Created: 2025-01-XX
 * Description: Google Maps 메인 컴포넌트 (@react-google-maps/api 사용)
 */

/// <reference types="vite/client" />

import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  GoogleMap as GoogleMapComponent,
  useLoadScript,
  InfoWindow,
  OverlayView,
} from '@react-google-maps/api';
import styled from '@emotion/styled';
import { useTheme } from '@/theme';
import type { GoogleMapProps, GoogleMapRef, MapMarker as MapMarkerType } from './types';
import { DeviceInfoCard } from '../device/DeviceInfoCard';

// ==================== Styled Components ====================
const MapContainer = styled.div<{ width?: string | number; height?: string | number }>`
  width: ${({ width }) => (typeof width === 'number' ? `${width}px` : width || '100%')};
  height: ${({ height }) => (typeof height === 'number' ? `${height}px` : height || '400px')};
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;

  /* InfoWindow X 버튼 숨기기 */
  .gm-ui-hover-effect {
    display: none !important;
  }

  /* InfoWindow 기본 카드 스타일 제거 (DeviceStatusCard가 자체 Card 스타일을 가짐) */
  .gm-style-iw-c {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
    border-radius: 0 !important;
  }

  .gm-style-iw-d {
    overflow: hidden !important;
    padding: 0 !important;
  }

  .gm-style-iw-t::after {
    display: none !important;
  }

  /* InfoWindow 내부 클릭 시 이벤트 전파 방지 */
  .gm-style-iw-c,
  .gm-style-iw-d {
    pointer-events: auto !important;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => {
    return `${theme.colors.surface.layout.overlay}CC`;
  }};
  z-index: 1000;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const Legend = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${({ theme }) => {
    return theme.colors.surface.layout.overlay;
  }};
  padding: 8px 12px;
  border-radius: 6px;
  box-shadow: 0 2px 6px
    ${({ theme }) => {
      return theme.colors.surface.layout.overlay;
    }};
  z-index: 1000;
  font-size: 11px;
  line-height: 1.4;
  display: none; /* 임시로 숨김 처리 */
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LegendIconWrapper = styled.div`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LegendIconCircle = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  border: 1px solid #ffffff;
`;

const LegendIconImage = styled.img`
  width: 12px;
  height: 12px;
`;

// ==================== Custom Marker with Device Image ====================
interface CustomMarkerProps {
  position: { lat: number; lng: number };
  color: string;
  icon?: string;
  deviceImageUrl?: string;
  hasError?: boolean;
  onClick: () => void;
}

/**
 * location.svg 기반 마커 아이콘 생성 (반반 색상 지원)
 */
const createLocationIcon = (baseColor: string, hasError: boolean): string => {
  const errorColor = '#ef4444'; // 빨강

  if (hasError) {
    // 반반 색상: 왼쪽은 baseColor, 오른쪽은 errorColor
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="leftHalf">
            <rect x="0" y="0" width="12" height="24"/>
          </clipPath>
          <clipPath id="rightHalf">
            <rect x="12" y="0" width="12" height="24"/>
          </clipPath>
        </defs>
        <!-- 왼쪽 절반: baseColor -->
        <g clip-path="url(#leftHalf)">
          <path d="M12,2a8,8,0,0,0-8,8c0,5.4,7,11.5,7.35,11.76l.65.56.65-.56C13,21.5,20,15.4,20,10A8,8,0,0,0,12,2Zm0,12a4,4,0,1,1,4-4A4,4,0,0,1,12,14Z" fill="${baseColor}"/>
          <circle cx="12" cy="10" r="2" fill="${baseColor}"/>
        </g>
        <!-- 오른쪽 절반: errorColor -->
        <g clip-path="url(#rightHalf)">
          <path d="M12,2a8,8,0,0,0-8,8c0,5.4,7,11.5,7.35,11.76l.65.56.65-.56C13,21.5,20,15.4,20,10A8,8,0,0,0,12,2Zm0,12a4,4,0,1,1,4-4A4,4,0,0,1,12,14Z" fill="${errorColor}"/>
          <circle cx="12" cy="10" r="2" fill="${errorColor}"/>
        </g>
      </svg>
    `)}`;
  } else {
    // 단색
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12,2a8,8,0,0,0-8,8c0,5.4,7,11.5,7.35,11.76l.65.56.65-.56C13,21.5,20,15.4,20,10A8,8,0,0,0,12,2Zm0,12a4,4,0,1,1,4-4A4,4,0,0,1,12,14Z" fill="${baseColor}"/>
        <circle cx="12" cy="10" r="2" fill="${baseColor}"/>
      </svg>
    `)}`;
  }
};

const CustomMarker: React.FC<CustomMarkerProps> = ({
  position,
  color,
  icon,
  deviceImageUrl,
  hasError = false,
  onClick,
}) => {
  // icon이 있으면 사용 (deviceToMapData에서 생성한 location.svg 기반 아이콘), 없으면 location.svg 단색 사용
  const markerIcon = icon || createLocationIcon(color, hasError);

  return (
    <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <div
        onClick={e => {
          e.stopPropagation();
          onClick();
        }}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
        }}
      >
        <img
          src={markerIcon}
          alt="marker"
          style={{
            width: '24px',
            height: '24px',
            display: 'block',
            flexShrink: 0,
          }}
        />
        {deviceImageUrl && (
          <img
            src={deviceImageUrl}
            alt="device"
            style={{
              marginLeft: '-4px',
              width: '36px',
              height: '36px',
              objectFit: 'contain',
              display: 'block',
              flexShrink: 0,
            }}
            onError={e => {
              console.warn('Device image failed to load:', deviceImageUrl);
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </div>
    </OverlayView>
  );
};

// ==================== Component ====================
export const GoogleMap = forwardRef<GoogleMapRef, GoogleMapProps>(
  (
    {
      apiKey: providedApiKey,
      center = { lat: 34.0522, lng: -118.2437 }, // Los Angeles 기본값
      zoom = 17, // 20m 정도로 확대된 기본 zoom 레벨
      markers = [],
      infoWindowData,
      height = '400px',
      width = '100%',
      mapStyles,
      onMarkerClick,
      onMapLoad,
      onCenterChanged,
      onZoomChanged,
    },
    ref
  ) => {
    // API 키: props로 제공된 경우 사용, 없으면 환경 변수에서 가져오기
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiKey = providedApiKey || (import.meta.env as any).VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.error(
        '❌ Google Maps API 키가 설정되지 않았습니다.\n' +
          '프론트엔드 .env 파일에 다음을 추가하세요:\n' +
          'VITE_GOOGLE_MAPS_API_KEY=your_api_key_here'
      );
    }

    const { themeMode } = useTheme();
    const [selectedMarkerId, setSelectedMarkerId] = useState<string | number | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);

    // Google Maps API 로드 상태
    const { isLoaded, loadError } = useLoadScript({
      id: 'google-map-script',
      googleMapsApiKey: apiKey || '',
    });

    // 다크 모드 지도 스타일
    const darkMapStyles: google.maps.MapTypeStyle[] = useMemo(
      () => [
        { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
        {
          featureType: 'administrative.locality',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#d59563' }],
        },
        {
          featureType: 'poi',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#d59563' }],
        },
        {
          featureType: 'poi.park',
          elementType: 'geometry',
          stylers: [{ color: '#263c3f' }],
        },
        {
          featureType: 'poi.park',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#6b9a76' }],
        },
        {
          featureType: 'road',
          elementType: 'geometry',
          stylers: [{ color: '#38414e' }],
        },
        {
          featureType: 'road',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#212a37' }],
        },
        {
          featureType: 'road',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#9ca5b3' }],
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry',
          stylers: [{ color: '#746855' }],
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#1f2835' }],
        },
        {
          featureType: 'road.highway',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#f3d19c' }],
        },
        {
          featureType: 'transit',
          elementType: 'geometry',
          stylers: [{ color: '#2f3948' }],
        },
        {
          featureType: 'transit.station',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#d59563' }],
        },
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [{ color: '#17263c' }],
        },
        {
          featureType: 'water',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#515c6d' }],
        },
        {
          featureType: 'water',
          elementType: 'labels.text.stroke',
          stylers: [{ color: '#17263c' }],
        },
      ],
      []
    );

    // 지도 옵션 - 테마에 따라 스타일 적용
    const mapOptions = useMemo(() => {
      // mapStyles가 제공되면 우선 사용, 없으면 테마에 따라 자동 적용
      const styles = mapStyles || (themeMode === 'dark' ? darkMapStyles : undefined);

      return {
        styles,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: true,
        scaleControl: true,
        streetViewControl: true,
        rotateControl: true,
        fullscreenControl: true,
      };
    }, [mapStyles, themeMode, darkMapStyles]);

    // 마커 클릭 핸들러
    const handleMarkerClick = useCallback(
      (marker: MapMarkerType) => {
        setSelectedMarkerId(marker.id);
        if (onMarkerClick) {
          onMarkerClick(marker);
        }
      },
      [onMarkerClick]
    );

    // 지도 클릭 핸들러 (정보창 닫기)
    const handleMapClick = useCallback(() => {
      setSelectedMarkerId(null);
    }, []);

    // 지도 로드 핸들러
    const handleMapLoad = useCallback(
      (map: google.maps.Map) => {
        mapRef.current = map;
        if (onMapLoad) {
          onMapLoad(map);
        }
      },
      [onMapLoad]
    );

    // ref를 통해 외부에서 호출할 수 있는 메서드들
    useImperativeHandle(
      ref,
      () => ({
        panToMarker: (markerId: string | number) => {
          const marker = markers.find(m => m.id === markerId);
          if (marker && mapRef.current) {
            const position = new google.maps.LatLng(marker.latitude, marker.longitude);
            mapRef.current.panTo(position);
            mapRef.current.setZoom(16); // 확대
            // 마커 클릭도 트리거하여 정보창 표시
            handleMarkerClick(marker);
          } else {
            console.warn('[GoogleMap] Marker not found or map not loaded:', {
              markerId,
              markerFound: !!marker,
              mapLoaded: !!mapRef.current,
            });
          }
        },
        panTo: (lat: number, lng: number, zoomLevel?: number) => {
          if (mapRef.current) {
            const position = new google.maps.LatLng(lat, lng);
            mapRef.current.panTo(position);
            if (zoomLevel !== undefined) {
              mapRef.current.setZoom(zoomLevel);
            } else {
              mapRef.current.setZoom(16);
            }
          }
        },
      }),
      [markers, handleMarkerClick]
    );

    // 지도 중심 변경 핸들러
    const handleCenterChanged = useCallback(() => {
      // @react-google-maps/api의 GoogleMap 컴포넌트는 center prop이 변경되면 자동으로 업데이트됨
      // 필요시 map 인스턴스를 통해 center를 가져올 수 있음
      if (onCenterChanged) {
        // map 인스턴스가 필요하지만 현재는 onMapLoad에서만 접근 가능
        // 필요시 ref를 통해 map 인스턴스를 저장해야 함
      }
    }, [onCenterChanged]);

    // 줌 변경 핸들러
    const handleZoomChanged = useCallback(() => {
      // @react-google-maps/api의 GoogleMap 컴포넌트는 zoom prop이 변경되면 자동으로 업데이트됨
      // 필요시 map 인스턴스를 통해 zoom을 가져올 수 있음
      if (onZoomChanged) {
        // map 인스턴스가 필요하지만 현재는 onMapLoad에서만 접근 가능
        // 필요시 ref를 통해 map 인스턴스를 저장해야 함
      }
    }, [onZoomChanged]);

    if (!apiKey) {
      return (
        <MapContainer width={width} height={height}>
          <LoadingOverlay>
            <div>Google Maps API key is required.</div>
          </LoadingOverlay>
        </MapContainer>
      );
    }

    // API 로드 중
    if (!isLoaded) {
      return (
        <MapContainer width={width} height={height}>
          <LoadingOverlay>Loading map...</LoadingOverlay>
        </MapContainer>
      );
    }

    // API 로드 에러
    if (loadError) {
      return (
        <MapContainer width={width} height={height}>
          <LoadingOverlay>
            <div>Failed to load map. Please check your API key.</div>
          </LoadingOverlay>
        </MapContainer>
      );
    }

    // 범례 아이콘 생성 (반반 색상 - 에러)
    const createLegendIcon = (baseColor: string, hasError: boolean) => {
      if (hasError) {
        const errorColor = '#ef4444';
        return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="12" height="12" xmlns="http://www.w3.org/2000/svg">
          <circle cx="6" cy="6" r="5" fill="${baseColor}"/>
          <path d="M 6 1 A 5 5 0 0 0 6 11 Z" fill="${errorColor}"/>
          <circle cx="6" cy="6" r="5" fill="none" stroke="#ffffff" stroke-width="1"/>
        </svg>
      `)}`;
      }
      return undefined;
    };

    // 범례 아이콘 생성 (반반 색상 - 워닝)
    const createLegendWarningIcon = (baseColor: string) => {
      const warningColor = '#f59e0b';
      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="12" height="12" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" r="5" fill="${baseColor}"/>
        <path d="M 6 1 A 5 5 0 0 0 6 11 Z" fill="${warningColor}"/>
        <circle cx="6" cy="6" r="5" fill="none" stroke="#ffffff" stroke-width="1"/>
      </svg>
    `)}`;
    };

    return (
      <MapContainer width={width} height={height}>
        {/* 범례 */}
        <Legend>
          <LegendItem>
            <LegendIconWrapper>
              <LegendIconCircle color="#10b981" />
            </LegendIconWrapper>
            <span>Connected</span>
          </LegendItem>
          <LegendItem>
            <LegendIconWrapper>
              <LegendIconCircle color="#6b7280" />
            </LegendIconWrapper>
            <span>Disconnected</span>
          </LegendItem>
          <LegendItem>
            <LegendIconWrapper>
              <LegendIconImage src={createLegendIcon('#10b981', true)} alt="Connected + Error" />
            </LegendIconWrapper>
            <span>Connected + Error</span>
          </LegendItem>
          <LegendItem>
            <LegendIconWrapper>
              <LegendIconImage src={createLegendIcon('#6b7280', true)} alt="Disconnected + Error" />
            </LegendIconWrapper>
            <span>Disconnected + Error</span>
          </LegendItem>
          <LegendItem>
            <LegendIconWrapper>
              <LegendIconImage src={createLegendWarningIcon('#10b981')} alt="Connected + Warning" />
            </LegendIconWrapper>
            <span>Connected + Warning</span>
          </LegendItem>
          <LegendItem>
            <LegendIconWrapper>
              <LegendIconImage
                src={createLegendWarningIcon('#6b7280')}
                alt="Disconnected + Warning"
              />
            </LegendIconWrapper>
            <span>Disconnected + Warning</span>
          </LegendItem>
        </Legend>

        <GoogleMapComponent
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={zoom}
          options={mapOptions}
          onLoad={handleMapLoad}
          onClick={handleMapClick}
          onCenterChanged={onCenterChanged ? handleCenterChanged : undefined}
          onZoomChanged={onZoomChanged ? handleZoomChanged : undefined}
        >
          {markers.map(marker => {
            const isSelected = selectedMarkerId === marker.id;
            const infoData = infoWindowData?.get(marker.id);
            const deviceImageUrl = marker.data?.deviceImageUrl as string | undefined;

            // 마커의 고유 키 생성 (위치, 색상, systemStatus, sensor_status 변경 감지를 위해)
            const systemStatus = marker.data?.systemStatus || '';
            const sensorStatuses = marker.data?.sensorStatuses || '';
            const hasError = marker.data?.hasError as boolean | undefined;
            const markerKey = `${marker.id}-${marker.latitude}-${marker.longitude}-${marker.color}-${marker.icon || ''}-${systemStatus}-${sensorStatuses}-${deviceImageUrl || ''}-${hasError || false}`;

            return (
              <React.Fragment key={markerKey}>
                <CustomMarker
                  position={{ lat: marker.latitude, lng: marker.longitude }}
                  color={marker.color || '#10b981'}
                  icon={marker.icon}
                  deviceImageUrl={deviceImageUrl}
                  hasError={hasError}
                  onClick={() => handleMarkerClick(marker)}
                />
                {isSelected && infoData && (
                  <InfoWindow position={{ lat: marker.latitude, lng: marker.longitude }}>
                    <DeviceInfoCard
                      data={{
                        name: infoData.name,
                        serialNumber: infoData.serialNumber,
                        deviceType: infoData.deviceType,
                        firmwareVersion: infoData.firmwareVersion,
                        deviceImageUrl: infoData.deviceImageUrl,
                        systemStatus: infoData.systemStatus,
                        systemStatusDetails: infoData.additionalInfo?.systemStatusDetails as
                          | string[]
                          | undefined,
                        sensors: infoData.sensors,
                        temperature: infoData.temperature,
                        batteryState: infoData.batteryState,
                        batteryStateValue: infoData.batteryStateValue,
                        timestamp: infoData.timestamp,
                        createdAt: infoData.createdAt,
                        rawMonitoringData: infoData.rawMonitoringData,
                        additionalInfo: infoData.additionalInfo,
                      }}
                    />
                  </InfoWindow>
                )}
              </React.Fragment>
            );
          })}
        </GoogleMapComponent>
      </MapContainer>
    );
  }
);
